import { Box, Divider, SxProps, Typography } from "@mui/material";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { logService } from "../../Core/Api/services";
import { ActivityKind } from "../../Core/Api/Types/activity-kind";
import { DbActivityInfos } from "../../Core/Api/Types/db-activity-infos";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import Activity, { ActivityMappedData } from "../Activity/activity";
import CommentForm from "../Misc/CommentForm/comment-form";
import TableSkeleton from "../Table/TableSkeleton";
import { useTranslation } from "react-i18next";

export interface ResourceNotificationProps {
  sx?: SxProps;
  resourceKind: ActivityKind;
  resourceId?: number;
  depth?: number;
  enableComments?: boolean;
}

export interface ResourceNotificationHandle {
  AddActivity: (activity: DbActivityInfos) => void;
}

const ResourceNotification = forwardRef<ResourceNotificationHandle, ResourceNotificationProps>((props, ref) => {
  const { t } = useTranslation();
  const { resourceKind, resourceId, depth, enableComments } = props;
  const [activities, setActivities] = useState<ActivityMappedData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const defaultDepth = 50;

  const enqueueCustomSnackbar = useCustomSnackbar();

  // Call methods from parent
  useImperativeHandle(ref, () => ({
    AddActivity(activity: DbActivityInfos) {
      addActivity(activity);
    },
  }));

  const addActivity = (activity: DbActivityInfos) => {
    let mapped = logService.GetMappedActivity(activity);
    setActivities((acts) => [mapped, ...acts]);
  };

  const handleCommentSend = (comment: string) => {
    logService
      .InsertActivityComment({
        message: comment,
        resId: resourceId!,
        kind: resourceKind,
        activityReplyId: null,
      })
      .then((response) => {
        enqueueCustomSnackbar(t("alert:success.commentHasBeenPosted"), "success");

        if (response.activity) addActivity(response.activity);
      });
  };

  useEffect(() => {
    if (resourceId !== undefined && resourceId !== 0)
      logService.ListResourceActivities(resourceKind, resourceId, 0, depth ? depth : defaultDepth).then((data) => {
        setActivities(data);
        setIsLoading(false);
      });
  }, [resourceKind, resourceId]);

  return (
    <Box mt={2}>
      {enableComments && enableComments === true && (
        <>
          <CommentForm onSend={(e) => handleCommentSend(e)} sx={{ mt: 2 }} />
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {activities.length > 0 ? (
        activities?.map((activity) => <Activity {...activity} sx={{ mb: 2 }} key={activity.id} />)
      ) : (
        <>{isLoading ? <TableSkeleton /> : <Typography>{t("common:noActivities")}</Typography>}</>
      )}
    </Box>
  );
});

export default ResourceNotification;
